import styles from "./template.css";
import template from "./template";
import { AoflElement, customElement, property } from "@aofl/element";
import { i18nMixin } from "@aofl/i18n-mixin";
import {
  sdoNs,
  reportTypes,
  printReportTypes,
} from "@modules/constants-enumerate";
import { SimpleModal } from "@modules/simple-modal";
import { storeInstance } from "@aofl/store/dist/legacy";
import { ApiService } from "@modules/api-service";
import "./sdo";
import translations from "../i18n";
import { Toast } from "@modules/toast";
import { toastStatus } from "@modules/i18n-labels";
import { wrapUnsafeHtml } from "@modules/wrap-unsafe-html";
import {
  CurrentProduct,
  launchStudentPlacementReport,
  launchClassroomPlacementReport,
  launchSchoolPlacementReport,
  launchDistrictPlacementReport,
  launchDistrictGrowthReport,
  launchSchoolGrowthReport,
  launchClassroomGrowthReport,
  launchStudentGrowthReport,
  ApiService as ComponentsApiService,
  isDemoState, AnalyticEvent, AnalyticsTrackerQueue,
} from '@epg/epg-components';

/**
 * @summary PrintReportsModal
 * @extends {{i18nMixin(AoflElement)}
 */
@customElement('print-reports-modal')
class PrintReportsModal extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of PrintReportsModal.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;
  }

  /**
   * @readonly
   */
  static is = 'print-reports-modal';

  @property({type: Object})
  data = {};

  @property({type: Number, mapState: `${sdoNs.SESSION}.settings.tier_structure.levels`, store: storeInstance})
  levels = 0;

  @property({type: Array, mapState: `${sdoNs.SESSION}.settings.tier_structure.labels`, store: storeInstance})
  tierLabels = [];

  @property({type: Array, mapState: `${sdoNs.PRINT_REPORTS}.students`, store: storeInstance})
  students = [];

  @property({type: Array, mapState: `${sdoNs.PRINT_REPORTS}.reports`, store: storeInstance})
  reportTypes = [];

  @property({type: Array, mapState: `${sdoNs.PRINT_REPORTS}.dates`, store: storeInstance})
  dates = [];

  @property({type: Array, mapState: `${sdoNs.PRINT_REPORTS}.$tiers`, store: storeInstance})
  tiers = [];

  @property({type: String, mapState: `${sdoNs.PRINT_REPORTS}.$reportDate`, store: storeInstance})
  reportDate = '';

  @property({type: String, mapState: `${sdoNs.PRINT_REPORTS}.$reportDatesByMonth`, store: storeInstance})
  reportDatesByMonth = [];

  @property({type: Object, mapState: `${sdoNs.PRINT_REPORTS}.selected`, store: storeInstance})
  selected = {};

  @property({type: Boolean})
  downloadDisabled = true;

  @property({type: Boolean})
  csvDisabled = true;

  @property({type: Boolean})
  showCsvMissingText = false;

  @property({type: Boolean})
  timeFrameDisabled = true;

  @property({type: Boolean})
  loading = true;

  @property({type: Object})
  selectedReportOptions = {};

  @property({type: String})
  downloadReportsURL = '';

  @property({type: Boolean})
  showGrades = true;

  @property({type: Array})
  grades = [];

  @property({type: Object})
  gradeMapping = {
    '': 'All Grades',
    'All Grades': 'All Grades',
    'prekindergarten': 'Pre-K/Preschool/TK',
    'kindergarten': 'Kindergarten',
    '1': '1st',
    '2': '2nd',
    '3': '3rd',
    '4': '4th',
    '5': '5th',
  };

  _product = '';

  closeModal() {
    SimpleModal.hide();
    const event = AnalyticEvent.create('reports.modal_close', {});
    AnalyticsTrackerQueue.enqueueEvent(event);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setInitialData();
  }

  setInitialData() {
    this._product = CurrentProduct.get();
    const groupIdArray = this.getGroupPath().split('.');
    groupIdArray.pop();
    this.selectGroup(
      {
        label: '',
        value: groupIdArray[groupIdArray.length - 1],
        dates: null,
        uuid: null,
      },
      -1
    );
    this.getNextDropdown();
  }

  selectOption(selected, sectionInfo) {
    storeInstance.commit({
      namespace: sdoNs.PRINT_REPORTS,
      mutationId: 'setSelected',
      payload: {
        selected,
        sectionInfo,
      }
    });
    this.getNextDropdown();
  }

  selectGroup(groupInfo, dropdownLevel) {
    storeInstance.commit({
      namespace: sdoNs.PRINT_REPORTS,
      mutationId: "setSelectedGroups",
      payload: {
        groupInfo,
        index: dropdownLevel,
      },
    });
    this.getNextDropdown();
  }

  showCsvButton(reportType) {
    return (
      (reportType === reportTypes.DISTRICT ||
        reportType === reportTypes.SCHOOL) &&
      this.selected.reportType?.value === printReportTypes.PLACEMENT
    );
  }

  async downloadCsv() {
    const groupIdArray = this.getGroupPath().split('.');
    groupIdArray.pop();
    const reportType = this.getReportType(
      groupIdArray.length,
      parseInt(this.levels, 10)
    );
    const groupIndex = this.selected.groups.length - 1;
    try {
      switch (reportType) {
        case reportTypes.DISTRICT:
          if (isDemoState()) {
            await ComponentsApiService.getDistrictPlacementDemoCsv(
              this._product
            );
          } else {
            await ComponentsApiService.getDistrictPlacementCsv(
              this.selected.groups[groupIndex].value,
              this._product
            );
          }
          break;
        case reportTypes.SCHOOL:
          if (isDemoState()) {
            await ComponentsApiService.getSchoolPlacementDemoCsv(
              this._product
            );
          } else {
            await ComponentsApiService.getSchoolPlacementCsv(
              this.selected.groups[groupIndex].value,
              this._product
            );
          }
          break;
      }
    } catch (e) {
      this.showCsvMissingText = true;
    }
  }

  viewReport() {
    const groupIdArray = this.getGroupPath().split(".");
    groupIdArray.pop();
    const reportType = this.getReportType(
      groupIdArray.length,
      parseInt(this.levels, 10)
    );
    this.launchReportLink(reportType, this.selected.reportType?.value);
  }

  launchReportLink(reportType, printReportType) {
    const grade = this.selected.grade !== '' && this.selected.grade !== 'All Grades' ? this.selected.grade : null;
    const reportIndex = this.selected.groups.length - 1;
    switch (reportType) {
      case reportTypes.DISTRICT:
        if (printReportType === printReportTypes.PLACEMENT) {
          launchDistrictPlacementReport(
            this.selected.groups[reportIndex].value,
            this._product
          );
          break;
        } else if (printReportType === printReportTypes.GROWTH) {
          launchDistrictGrowthReport(
            this.selected.groups[reportIndex].value,
            this._product
          );
          break;
        }
        break;
      case reportTypes.SCHOOL:
        if (printReportType === printReportTypes.PLACEMENT) {
          launchSchoolPlacementReport(
            this.selected.groups[reportIndex].value,
            this._product
          );
          break;
        } else if (printReportType === printReportTypes.GROWTH) {
          launchSchoolGrowthReport(
            this.selected.groups[reportIndex].value,
            this._product
          );
          break;
        }
        break;
      case reportTypes.CLASSROOM:
        if (printReportType === printReportTypes.PLACEMENT) {
          launchClassroomPlacementReport(
            this.selected.groups[reportIndex].value,
            this._product,
            grade
          );
          break;
        } else if (printReportType === printReportTypes.GROWTH) {
          launchClassroomGrowthReport(
            this.selected.groups[reportIndex].value,
            this._product,
            grade || ''
          );
          break;
        }
        break;
      case reportTypes.STUDENT:
        if (printReportType === printReportTypes.PLACEMENT) {
          launchStudentPlacementReport(
            this.selected.student.uuid,
            this._product,
            this.selected.groups[reportIndex].value
          );
          break;
        } else if (printReportType === printReportTypes.GROWTH) {
          launchStudentGrowthReport(
            this.selected.student.uuid,
            this._product,
            this.selected.groups[reportIndex].value
          );
          break;
        }
        break;
    }
  }

  getReportType(level, tierLevels) {
    const tierLevelMap = {
      1: {
        1: reportTypes.SCHOOL,
        2: reportTypes.CLASSROOM,
      },
      2: {
        1: reportTypes.DISTRICT,
        2: reportTypes.SCHOOL,
        3: reportTypes.CLASSROOM,
      },
      3: {
        1: reportTypes.DISTRICT,
        2: reportTypes.DISTRICT,
        3: reportTypes.SCHOOL,
        4: reportTypes.CLASSROOM,
      },
      4: {
        1: reportTypes.DISTRICT,
        2: reportTypes.DISTRICT,
        3: reportTypes.DISTRICT,
        4: reportTypes.SCHOOL,
        5: reportTypes.CLASSROOM,
      },
    };

    if (
      this.selected?.student &&
      Object.keys(this.selected.student).length > 0
    ) {
      // student level
      return reportTypes.STUDENT;
    }

    return tierLevelMap[tierLevels][level] || reportTypes.DISTRICT;
  }

  getGroupPath() {
    const state = storeInstance.getState();
    const groupInfo = state[sdoNs.SESSION].group_info;
    let path = groupInfo.path + groupInfo.id + '.';
    this.selected.groups.map((group) => {
      path += group.value + '.';
    });
    return path;
  }

  async getNextDropdown() {
    const path = this.getGroupPath();
    this.loading = true;
    const resp = await ApiService.helpResourceGetNextDropdownInfo({
      group_path: path,
      student_id: this.selected.student?.value,
      product: this._product,
      report_id: this.selected.reportType?.value,
      date: this.selected.date?.value
    });

    this.loading = false;
    if (
      this.selected.reportType?.value === printReportTypes.PLACEMENT ||
      this.selected.reportType?.value === printReportTypes.GROWTH
    ) {
      this.downloadDisabled = false;
    } else {
      this.downloadDisabled = true;
    }

    const groupIdArray = this.getGroupPath().split('.');
    groupIdArray.pop();
    const reportType = this.getReportType(
      groupIdArray.length,
      parseInt(this.levels, 10)
    );
    this.showCsvMissingText = false;
    if (this.showCsvButton(reportType)) {
      this.csvDisabled = false;
    } else {
      this.csvDisabled = true;
    }

    this.showGrades = false;
    if (reportType === reportTypes.CLASSROOM) {
      const resp = await ApiService.classroomGroupGetGrades(
        this.selected.groups[this.selected.groups.length - 1].value,
        this._product
      );
      this.grades = resp.grades;
      if (resp.is_multi_grade) {
        let grades = ['All Grades'];
        grades = grades.concat(resp.grades);
        this.grades = grades;
      }
      this.showGrades = resp.is_multi_grade;
    }

    storeInstance.commit({
      namespace: sdoNs.PRINT_REPORTS,
      mutationId: 'setNextDropdowns',
      payload: resp.dropdowns
    });
  }

  setGrade(grade) {
    storeInstance.commit({
      namespace: sdoNs.PRINT_REPORTS,
      mutationId: 'setGrade',
      payload: grade
    });
  }

  showReportDownloadedToast(count, reportType) {
    Toast.success(wrapUnsafeHtml(toastStatus.reportDownloaded(count, reportType)));
  }

  disconnectedCallback() {
    storeInstance.commit({
      namespace: sdoNs.PRINT_REPORTS,
      mutationId: 'init',
    });
  }

  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default PrintReportsModal;
